/**
 * The login page's passkey affordance.
 *
 * Visibility uses synchronous host-shell and WebAuthn capability checks. The
 * default appearance uses a per-origin hint to choose prominence; the tile
 * variant keeps the method visible alongside the other web sign-in options.
 *
 * The native shell gets no affordance at all. Its WKWebView exposes
 * `PublicKeyCredential` and `credentials.create/get`, so a capability probe says
 * yes, but the ceremony can never complete: WebAuthn in a webview is gated behind
 * Apple's `com.apple.developer.web-browser` entitlement, which only full browsers
 * receive. Offering the button there turns a platform limit into a rejected
 * ceremony the copy then blames on the user's device. The shell check lives here
 * rather than inside `isPasskeySupported()`: that helper answers whether the Web
 * API EXISTS, never whether the host shell is allowed to use it. Folding one
 * into the other conflates two independent facts, and a native ASAuthorization
 * bridge needs the helper free to keep answering for the API alone.
 *
 * Extracted from `login.tsx` rather than inlined: that file sits close to the
 * 300-line ESLint cap, and the a11y wiring here (busy state, live region, focus
 * return after a cancelled ceremony) is cohesive enough to own a file.
 */

import { Key01, Lock01 } from '@untitled-ui/icons-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AUTH_SECONDARY_BUTTON_CLASS,
  AUTH_TILE_BUTTON_CLASS,
} from '@/components/auth/auth-button-styles';
import { AUTH_LINK_CLASS } from '@/components/auth/auth-styles';
import { isNativePlatform } from '@/lib/mobile/platform';
import { hasPasskeyHint } from '@/lib/passkey-hint';
import { isPasskeySupported } from '@/lib/passkeys';

import type { PasskeySignInNotice } from '@/components/auth/use-passkey-sign-in';

/**
 * Default trigger appearance supplied by the host. The web login's tile variant
 * uses shared provider-tile styling; capability checks apply to both variants.
 */
export interface PasskeyButtonChrome {
  /** Full-weight trigger, for when a passkey has been seen on this device. */
  readonly prominent: string;
  /** Low-emphasis trigger, for when it has not. */
  readonly subtle: string;
  /** Wrapper, so the affordance sits in the host's own vertical rhythm. */
  readonly container: string;
}

const WEB_AUTH_CHROME: PasskeyButtonChrome = {
  prominent: `${AUTH_SECONDARY_BUTTON_CLASS} text-sm font-medium text-[var(--color-fg-primary)]`,
  subtle: `${AUTH_LINK_CLASS} mx-auto block`,
  container: 'mt-2.5',
};

interface PasskeySignInButtonProps {
  readonly isPending: boolean;
  readonly isDisabled: boolean;
  readonly notice: PasskeySignInNotice;
  readonly onSignIn: () => void;
  /** Re-runs an MFA step-up check that could not answer. */
  readonly onRetry: () => void;
  /** Defaults to the web auth page's chrome. */
  readonly chrome?: PasskeyButtonChrome;
  readonly variant?: 'default' | 'tile';
}

/** Inline copy per notice. `null` renders nothing at all. */
const NOTICE_KEYS: Readonly<Record<PasskeySignInNotice, string | null>> = {
  none: null,
  cancelled: 'passkey.cancelledNote',
  not_found: 'passkey.notFoundNote',
  disabled: 'passkey.disabledNote',
  check_failed: 'passkey.checkFailedNote',
};

function passkeyPresentation(tile: boolean, prominent: boolean, chrome: PasskeyButtonChrome) {
  const accessibleLabel = prominent ? 'passkey.signIn' : 'passkey.useLink';
  const defaultClass = prominent ? chrome.prominent : chrome.subtle;
  return {
    accessibleLabel,
    label: tile ? 'passkey.shortLabel' : accessibleLabel,
    buttonClass: tile ? AUTH_TILE_BUTTON_CLASS : defaultClass,
    showIcon: tile || prominent,
  };
}

export function PasskeySignInButton({
  isPending,
  isDisabled,
  notice,
  onSignIn,
  onRetry,
  chrome = WEB_AUTH_CHROME,
  variant = 'default',
}: PasskeySignInButtonProps) {
  const { t } = useTranslation('auth');
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Snapshotted on mount. Re-reading per render would let the affordance change
  // shape under the user: clearing the hint after a vanished credential is about
  // the NEXT visit, not this one. `useRef` is not an option (both are read during
  // render, which `react-hooks/refs` forbids) and a setter-less `useState` trips
  // `react/hook-use-state`, so a memo with no deps is what fits.
  const available = useMemo(() => !isNativePlatform() && isPasskeySupported(), []);
  const prominent = useMemo(() => hasPasskeyHint(), []);

  useEffect(() => {
    if (notice === 'cancelled') {
      // The browser's passkey sheet takes focus and a cancel drops it on the
      // body, leaving a keyboard user nowhere.
      triggerRef.current?.focus();
    }
  }, [notice]);

  if (!available) {
    return null;
  }

  const tile = variant === 'tile';
  const presentation = passkeyPresentation(tile, prominent, chrome);
  const noticeKey = NOTICE_KEYS[notice];
  const noticeText = noticeKey === null ? '' : t(noticeKey);

  return (
    <div className={tile ? 'contents' : chrome.container}>
      <button
        ref={triggerRef}
        aria-busy={isPending}
        aria-label={t(presentation.accessibleLabel)}
        className={`${presentation.buttonClass} aria-busy:disabled:opacity-100`}
        disabled={isDisabled}
        type="button"
        onClick={onSignIn}
      >
        {isPending ? (
          <span
            aria-hidden="true"
            className="relative flex size-5 shrink-0 items-center justify-center text-brand-secondary"
          >
            <svg className="absolute size-7" fill="none" viewBox="0 0 28 28">
              <circle
                className="stroke-current opacity-15"
                cx="14"
                cy="14"
                r="12"
                strokeWidth="1.5"
              />
              <circle
                className="origin-center stroke-current motion-safe:animate-[spin_2.4s_linear_infinite]"
                cx="14"
                cy="14"
                r="12"
                strokeDasharray="18 76"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
            <Lock01 className="size-3.5" />
          </span>
        ) : null}
        {presentation.showIcon && !isPending ? (
          <Key01 aria-hidden="true" className="size-5 shrink-0 text-[var(--color-fg-tertiary)]" />
        ) : null}
        {t(presentation.label)}
      </button>

      {noticeKey === null ? null : (
        <p
          className="col-span-full mt-2 text-center text-xs text-quaternary"
          data-testid="passkey-notice"
        >
          {noticeText}
          {notice === 'check_failed' ? (
            <button className={`${AUTH_LINK_CLASS} ml-1.5`} type="button" onClick={onRetry}>
              {t('passkey.retry')}
            </button>
          ) : null}
        </p>
      )}

      {/* Always mounted: a live region added at the same time as its content is
          not reliably announced. */}
      <output aria-live="polite" className="sr-only">
        {isPending ? t('passkey.waiting') : noticeText}
      </output>
    </div>
  );
}
